import React, { useState } from 'react';
import { Badge } from '../ui/Badge';
import { Shield, ShieldCheck, ShieldX, Clock } from 'lucide-react';
import { AuditStatus } from '../../hooks/useSmartContractTemplates';

interface AuditStatusBadgeProps {
  status: AuditStatus;
  auditorName?: string | null;
  auditDate?: string | null;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const statusConfig: Record<AuditStatus, {
  label: string;
  variant: 'success' | 'warning' | 'error';
  icon: React.ReactNode;
  description: string;
}> = {
  PENDING_AUDIT: {
    label: 'Pendente',
    variant: 'warning',
    icon: <Clock className="w-3 h-3" />,
    description: 'Este template ainda não foi auditado. Só pode ser usado em redes de teste.',
  },
  AUDITED: {
    label: 'Auditado',
    variant: 'success',
    icon: <ShieldCheck className="w-3 h-3" />,
    description: 'Este template foi auditado e pode ser usado em redes de produção.',
  },
  AUDIT_FAILED: {
    label: 'Falhou',
    variant: 'error',
    icon: <ShieldX className="w-3 h-3" />,
    description: 'A auditoria deste template falhou. Não pode ser usado em produção.',
  },
};

export const AuditStatusBadge: React.FC<AuditStatusBadgeProps> = ({
  status,
  auditorName,
  auditDate,
  showDetails = false,
  size = 'md',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const config = statusConfig[status];
  
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const badge = (
    <Badge variant={config.variant} className="inline-flex items-center gap-1">
      {config.icon}
      <span>{config.label}</span>
    </Badge>
  );

  if (showDetails && (auditorName || auditDate)) {
    return (
      <div className="flex items-center gap-2">
        <div 
          className="relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {badge}
          {showTooltip && (
            <div className="absolute z-50 bottom-full left-0 mb-2 w-64 p-3 bg-surface-700 border border-surface-600 rounded-lg shadow-xl">
              <p className="text-sm font-medium text-surface-100 mb-1">{config.description}</p>
              {auditorName && (
                <p className="text-xs text-surface-400">
                  <span className="text-surface-300">Auditor:</span> {auditorName}
                </p>
              )}
              {auditDate && (
                <p className="text-xs text-surface-400">
                  <span className="text-surface-300">Data:</span> {formatDate(auditDate)}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="text-xs text-surface-400">
          {auditorName && <span>{auditorName}</span>}
          {auditorName && auditDate && <span> • </span>}
          {auditDate && <span>{formatDate(auditDate)}</span>}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {badge}
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-0 mb-2 w-64 p-3 bg-surface-700 border border-surface-600 rounded-lg shadow-xl">
          <p className="text-sm text-surface-300">{config.description}</p>
        </div>
      )}
    </div>
  );
};

export default AuditStatusBadge;
